import clsx from "clsx";
import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, useMemo } from "react";

import type { LonLat } from "../../api/types";
import { motion } from "../../components/presence";
import { t } from "../../i18n/i18n";
import { revealPaint, useFadeIn } from "../reveal";
import { FRIEND_BLUE } from "../units/symbols";
import { axisTagText, formatAreaKm2 } from "./format";
import { boxOf, corridorWidthM, extensionPreview, FOOTPRINT_ERRORS, isZoneLevel, orientedRing, preparedFootprint } from "./footprint";
import { useOpenZone } from "./useZones";
import { useZoneDraw } from "./zoneDraw";
import { zoneBusy } from "./zoneState";

/** Couches des tracés de la zone libre (docs/14 §2.7), montées quel que soit le camp affiché :
 *  - pendant le tracé de l'axe : extrémité arrière posée, fuseau prévu en tirets bleu ami (APP-6 : prévu) à la
 *    largeur doctrinale jusqu'au pointeur, empreinte préparée en gris tireté, puce de longueur et de surface (ton
 *    d'alerte et règle hors bornes) ;
 *  - axe accepté en attente de « Préparer le terrain » : le même dessin, figé ;
 *  - extension : emprise tracée (deux coins) en tirets d'action et surface ajoutée ;
 *  - zone ouverte en préparation (contour pas encore servi) ou en extension : son empreinte en gris tireté.
 *  Le contour gris n'est pas une mesure tactique : pas de couleur APP-6. Entrée en fondu (200 ms). */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

const FADE_MS = 200;
const FOOTPRINT_GREY = "#8a909c";
const ACTION_BLUE = "#2f6fed"; // jeton accent
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });
const polygon = (ring: LonLat[], properties: Record<string, unknown> = {}): Feature => ({
  type: "Feature",
  geometry: { type: "Polygon", coordinates: [ring] },
  properties,
});
const line = (coordinates: LonLat[]): Feature => ({ type: "Feature", geometry: { type: "LineString", coordinates }, properties: {} });
const point = (at: LonLat): Feature => ({ type: "Feature", geometry: { type: "Point", coordinates: at }, properties: {} });
const boxRing = ([w, s, e, n]: [number, number, number, number]): LonLat[] => [
  [w, s],
  [e, s],
  [e, n],
  [w, n],
  [w, s],
];

export function ZoneDrawLayers({ Source, Layer, Marker }: EngineParts) {
  const drawing = useZoneDraw((s) => s.drawing);
  const axis = useZoneDraw((s) => s.axis);
  const extension = useZoneDraw((s) => s.extension);
  const { zone } = useOpenZone();

  const view = useMemo(() => {
    const corridor: Feature[] = [];
    const footprint: Feature[] = [];
    const ends: Feature[] = [];
    const boxes: Feature[] = [];
    let tag: { at: LonLat; text: string; refused: boolean } | null = null;

    const drawAxis = (start: LonLat, end: LonLat, level: string, levelKey: string | null, mission: string | null, live: boolean) => {
      const result = preparedFootprint(start, end, level, levelKey);
      if (isZoneLevel(level)) {
        const ring = orientedRing(start, end, 0, corridorWidthM(level, levelKey) / 2);
        corridor.push(polygon(ring));
      }
      corridor.push(line([start, end]));
      const fp = result.footprint;
      if (fp) footprint.push(polygon(fp.ring, { refused: !result.ok }));
      ends.push(point(start), point(end));
      if (live) tag = { at: end, ...axisTagText(result, level, mission) };
    };

    if (drawing?.mode === "axis") {
      const [start] = drawing.points;
      if (start && drawing.hover) drawAxis(start, drawing.hover, drawing.level, drawing.levelKey, drawing.mission, true);
      else if (start) ends.push(point(start));
    } else if (axis) {
      drawAxis(axis.start, axis.end, axis.level, axis.levelKey, axis.mission, false);
    }

    if (drawing?.mode === "extend") {
      footprint.push(polygon(drawing.ring));
      const [corner] = drawing.points;
      if (corner && drawing.hover) {
        const bbox = boxOf(corner, drawing.hover);
        const preview = extensionPreview(drawing.ring, bbox, drawing.level);
        boxes.push(polygon(boxRing(bbox), { refused: !preview.ok }));
        tag = {
          at: drawing.hover,
          refused: !preview.ok,
          text: preview.ok
            ? t("+{added} · terrain {area}", { added: formatAreaKm2(preview.added_km2), area: formatAreaKm2(preview.area_km2) })
            : t(FOOTPRINT_ERRORS[preview.error ?? "invalid"]),
        };
      } else if (corner) ends.push(point(corner));
    } else if (extension) {
      boxes.push(polygon(boxRing(extension.bbox)));
    }

    // Zone ouverte : empreinte tant que son contour n'est pas servi (préparation), et celle de l'extension en cours.
    if (zone && !drawing && !axis) {
      if (zone.status !== "ready" && zone.footprint) footprint.push(polygon(zone.footprint.coordinates[0]!));
      if (zoneBusy(zone) && zone.pending_footprint) footprint.push(polygon(zone.pending_footprint.coordinates[0]!));
    }
    return {
      corridor: collection(corridor),
      footprint: collection(footprint),
      ends: collection(ends),
      boxes: collection(boxes),
      tag: tag as { at: LonLat; text: string; refused: boolean } | null,
    };
  }, [drawing, axis, extension, zone]);

  const key = drawing ? `${drawing.mode}` : axis ? "axis" : extension ? "extension" : zone ? `zone-${zone.id}-${zone.status}` : null;
  const on = useFadeIn(key);
  const has = (c: FeatureCollection) => c.features.length > 0;

  return (
    <>
      <Source id="gx-zone-footprint" type="geojson" data={has(view.footprint) ? view.footprint : EMPTY}>
        <Layer
          id="zone-footprint-fill"
          type="fill"
          paint={revealPaint("fill", { "fill-color": FOOTPRINT_GREY, "fill-opacity": ["case", ["==", ["get", "refused"], true], 0.12, 0.06] }, on, FADE_MS)}
        />
        <Layer
          id="zone-footprint-edge"
          type="line"
          paint={revealPaint("line", { "line-color": FOOTPRINT_GREY, "line-width": 1.25, "line-dasharray": [3, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-zone-corridor" type="geojson" data={has(view.corridor) ? view.corridor : EMPTY}>
        <Layer
          id="zone-corridor"
          type="line"
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 2, "line-dasharray": [2.5, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-zone-boxes" type="geojson" data={has(view.boxes) ? view.boxes : EMPTY}>
        <Layer
          id="zone-box-fill"
          type="fill"
          paint={revealPaint("fill", { "fill-color": ACTION_BLUE, "fill-opacity": ["case", ["==", ["get", "refused"], true], 0.04, 0.08] }, on, FADE_MS)}
        />
        <Layer
          id="zone-box-edge"
          type="line"
          paint={revealPaint("line", { "line-color": ACTION_BLUE, "line-width": 1.5, "line-dasharray": [2, 1.5] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-zone-ends" type="geojson" data={has(view.ends) ? view.ends : EMPTY}>
        <Layer
          id="zone-ends"
          type="circle"
          paint={revealPaint(
            "circle",
            { "circle-radius": 5, "circle-color": "#ffffff", "circle-stroke-color": FRIEND_BLUE, "circle-stroke-width": 2 },
            on,
            FADE_MS,
          )}
        />
      </Source>
      {view.tag && (
        <Marker longitude={view.tag.at[0]} latitude={view.tag.at[1]} anchor="bottom" offset={[0, -14]} style={{ pointerEvents: "none" }}>
          <div
            aria-hidden
            data-zone-tag=""
            className={clsx(
              motion("zoom"),
              "pointer-events-none flex h-6 items-center rounded-md border px-2 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.14)]",
              view.tag.refused ? "border-warn/30 bg-warn-soft text-warn" : "border-line-strong bg-surface text-ink",
            )}
          >
            <span className="text-meta leading-none font-medium tabular-nums">{view.tag.text}</span>
          </div>
        </Marker>
      )}
    </>
  );
}
